import { ValueDepreciationCalculator } from './value-deprication.calculator';
import { FuelCalculator } from './fuel.calculator';
import type { ICarInformation } from '../models/car-information.model';
import type { ITravelInformation } from '../components/TravelInformation';
import type { IVariableCostResult } from '../models/result.model';
import { CarUser } from '../models/car-user.enum';
import { ValueDepreciationScenario } from '../models/value-depreciation-scenario.enum';

export class VariableCarCostsCalculator {
  static calculateCostPerMonth(parkingFeeAndCongestionTaxCost: number, monthlyFuelCost: number, variableDepreciationPerMonth: number, travelDeductionSekPerYear: number): number {
    return Math.round(parkingFeeAndCongestionTaxCost + monthlyFuelCost + variableDepreciationPerMonth - (travelDeductionSekPerYear / 12 || 0));
  }

  static calculateParkingFeeAndCongestionTaxCostPerMonth(parkingFeeSekPerDay: number | undefined, congestionTaxSekPerDay: number | undefined, travelDaysPerMonth: number): number {
    return Math.round(((parkingFeeSekPerDay || 0) * travelDaysPerMonth || 0) + (congestionTaxSekPerDay || 0) * (travelDaysPerMonth || 0));
  }

  static calculateDistanceRatio(distanceToWorkKm: number, travelDaysPerMonth: number, drivingDistancePerYearKm: number): number {
    if (drivingDistancePerYearKm === 0) {
      return 0;
    }
    return Math.min(100, Math.round(((distanceToWorkKm * travelDaysPerMonth * 2 * 12) / drivingDistancePerYearKm) * 100));
  }

  public static calculateCost(carInformationParams: ICarInformation, travelInformationParams: ITravelInformation): IVariableCostResult {
    if (!travelInformationParams.travelDaysPerMonth || !carInformationParams.newCarPriceSek || !carInformationParams.fuelType || !travelInformationParams.distanceFromHomeToWorkKmState) {
      throw new Error('Missing required parameters');
    }

    const parkingFeeAndCongestionTaxCost = this.calculateParkingFeeAndCongestionTaxCostPerMonth(
      travelInformationParams.parkingFeeSekPerDay || 0,
      travelInformationParams.congestionTaxSekPerDay || 0,
      travelInformationParams.travelDaysPerMonth
    );
    const variableDepreciationPerMonth =
      carInformationParams.variableDepreciationPercentage && carInformationParams.carUser === CarUser.CarOwner
        ? ValueDepreciationCalculator.calculateVariableDepreciationCostPerMonth(
            {
              newCarPriceSek: carInformationParams.newCarPriceSek,
              carAgeInYears: carInformationParams.modelYear ? new Date().getFullYear() - parseInt(carInformationParams.modelYear, 10) : 0,
              valueDepreciationScenario: carInformationParams.estimatedCarValueDeprication || ValueDepreciationScenario.Normal,
            },
            carInformationParams.variableDepreciationPercentage
          )
        : 0;

    const monthlyFuelCost: number = FuelCalculator.calculateMonthlyFuelCost({
      consumptionUnitsPer10Km: carInformationParams.fuelConsumptionUnitPerKm || 0,
      fuelPriceSek: carInformationParams.fuelPriceSekPerUnit || 0,
      fuelType: carInformationParams.fuelType,
      travelDaysPerMonth: travelInformationParams.travelDaysPerMonth,
      distanceToWorkKm: travelInformationParams.distanceFromHomeToWorkKmState,
      workDrivingDistancePerMonthKm: travelInformationParams.distanceFromHomeToWorkKmState * travelInformationParams.travelDaysPerMonth * 2,
      drivingDistancePerYearKm: carInformationParams.drivingDistancePerYearKm,
    });

    const costPerMonth = this.calculateCostPerMonth(parkingFeeAndCongestionTaxCost, monthlyFuelCost, variableDepreciationPerMonth, travelInformationParams.travelDeductionSekPerYear || 0);

    return {
      costPerMonth: costPerMonth > 0 ? costPerMonth : 0,
      variableDepreciation: variableDepreciationPerMonth,
      fuelCost: monthlyFuelCost,
    };
  }
}

